<script setup lang="ts">
import { api } from '@/api';
import { TrackItDataSequenceInfoDto, TrackItMeasurementInfoDto } from '@/api/generator';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import { fullName } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { ref } from 'vue';
import TrackItDataPointsTable from './TrackItDataPointsTable.vue';
import TrackItMeasurementDataTable from './TrackItMeasurementDataTable.vue';
import TrackItMeasurementParametersTable from './TrackItMeasurementParametersTable.vue';

const props = defineProps<{
  measurement: TrackItMeasurementInfoDto;
}>();

const expanded = ref(false);
const measurementData = ref<TrackItDataSequenceInfoDto[]>([]);
const measurementDataLoaded = ref(false);
const measurementDataLoading = ref(false);

// The measurement DTO does not carry its data sequences, so they are fetched on first expand
// rather than on mount — the panel starts collapsed and most are never opened. `loaded` is only
// set on success, so a failed request is retried the next time the panel is opened.
async function toggle() {
  expanded.value = !expanded.value;

  if (
    !expanded.value ||
    measurementDataLoaded.value ||
    measurementDataLoading.value ||
    props.measurement.id == null
  ) {
    return;
  }

  measurementDataLoading.value = true;
  try {
    // `handle` always resolves — a failure arrives as { success: false, result: undefined }
    // rather than a rejection, so the outcome has to be checked rather than caught.
    const { result, success } = await api.trackItImportApi.getDataSequencesForMeasurement({
      measurementId: props.measurement.id,
    });

    if (success) {
      measurementData.value = result ?? [];
      measurementDataLoaded.value = true;
    }
  } finally {
    measurementDataLoading.value = false;
  }
}
</script>

<template>
  <div>
    <button
      type="button"
      class="d-flex align-center trackit-measurement-toggle"
      :aria-expanded="expanded"
      @click="toggle"
    >
      <v-icon :icon="expanded ? mdiChevronUp : mdiChevronDown" class="mr-1" />
      <span>Track-It Measurement</span>
    </button>

    <v-expand-transition>
      <div v-if="expanded" class="mt-2">
        <v-row dense>
          <v-col cols="6" lg="4">
            <v-label>{{ $t('MeasurementDate') }}</v-label>
            <div><DateTimeFormatter :value="measurement.measurementDate" /></div>
          </v-col>

          <v-col cols="6" lg="4">
            <v-label>{{ $t('TrackItAlias') }}</v-label>
            <div>{{ measurement.radiationUnit }}</div>
          </v-col>

          <v-col cols="6" lg="4">
            <v-label>{{ $t('TrackItMeasuringDevice') }}</v-label>
            <div>{{ measurement.measurementDevice }}</div>
          </v-col>

          <v-col cols="6" lg="4">
            <v-label>{{ $t('TrackItMeasuringSoftware') }}</v-label>
            <div>{{ measurement.measurementSoftware }}</div>
          </v-col>

          <v-col cols="6" lg="4">
            <v-label>{{ $t('ImportDate') }}</v-label>
            <div><DateTimeFormatter :value="measurement.importDate" /></div>
          </v-col>

          <v-col cols="6" lg="4">
            <v-label>{{ $t('ImportUser' as I18nKey) }}</v-label>
            <div>{{ fullName(measurement.importUser ?? undefined) }}</div>
          </v-col>
        </v-row>

        <v-row>
          <v-col v-if="measurement.measurementParameters?.length" cols="6">
            <TrackItMeasurementParametersTable
              :title="$t('Parameter_other')"
              :parameters="measurement.measurementParameters"
            />
          </v-col>

          <v-col v-if="measurement.dataTypes?.length" cols="6">
            <TrackItDataPointsTable
              title="Track-It Data Points"
              :dataPoints="measurement.dataTypes"
            />
          </v-col>
        </v-row>

        <!--
          Fetched on expand rather than shipped with the measurement DTO. The data sequences carry
          the scalars, the Profile/PDD curves and the MCC/XCC/UserDefined file payloads, so this
          one table covers all three — a value, a chart button or a download button per row.
        -->
        <v-row v-if="measurementDataLoading || measurementDataLoaded">
          <v-col cols="12">
            <v-progress-linear v-if="measurementDataLoading" indeterminate />

            <TrackItMeasurementDataTable
              v-else
              :title="$t('TrackItMeasurementData' as I18nKey)"
              :measurementData="measurementData"
            />
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped lang="scss">
.trackit-measurement-toggle {
  cursor: pointer;
  user-select: none;
  width: fit-content;

  // Strip the user-agent button chrome: this is a native button for keyboard and screen-reader
  // semantics, but it renders as the inline icon-plus-label heading it replaced.
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: left;
}
</style>
